<template>
  <div class="flex flex-col gap-4">
    <div class="flex flex-col gap-1">
      <p class="text-sm font-semibold text_color">
        {{ COPY.DNS_PANEL_TITLE }}
      </p>
      <p class="text-xs secondary_color">
        {{ COPY.DNS_PANEL_SUBTITLE }}
      </p>
    </div>

    <div class="a_records_container flex flex-col gap-2 p-3 rounded-md">
      <div class="grid grid-cols-[auto_1fr_auto_auto] gap-x-3 gap-y-2 text-xs">
        <div class="secondary_color font-semibold">Type</div>
        <div class="secondary_color font-semibold">Host</div>
        <div class="secondary_color font-semibold">Points to</div>
        <div></div>

        <template
          v-for="(ip, idx) in expectedARecords"
          :key="`${recordId}_${idx}`"
        >
          <div class="text_color">A</div>
          <div class="text_color truncate" :title="domain">{{ domain }}</div>
          <div class="text_color font-mono">{{ ip }}</div>
          <div class="flex items-center gap-1.5">
            <ThemeImage
              v-if="currentHasMatch(ip)"
              :icon="imageNames.CHECK_GREEN"
              alt="matched"
              class="size-3"
            />
            <button
              :id="`copy_a_record_${idx}`"
              type="button"
              class="more_actions_wrapper"
              @click="copyValue(ip)"
            >
              <ThemeImage :icon="imageNames.COPY_GREY" alt="copy" class="size-3" />
            </button>
          </div>
        </template>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div
        v-for="step in PROVISIONING_STEPS"
        :key="step.key"
        class="flex items-center gap-2"
      >
        <ThemeImage
          :icon="stepIcon(step.key)"
          :alt="step.label"
          class="size-4"
        />
        <span
          :class="[
            'text-xs',
            stepReached(step.key) ? 'text_color font-medium' : 'secondary_color',
          ]"
        >
          {{ step.label }}
        </span>
      </div>
    </div>

    <div
      v-if="isFailed"
      class="p-3 rounded-md failed_banner flex items-start gap-2"
    >
      <ThemeImage :icon="imageNames.WARNING" alt="failed" class="size-4 mt-0.5" />
      <div class="flex flex-col gap-0.5">
        <p class="text-sm font-semibold text_color">
          {{ errorMessage || COPY.FAILED_FALLBACK }}
        </p>
        <p class="text-xs secondary_color">{{ COPY.FAILED_FALLBACK }}</p>
      </div>
    </div>

    <div
      v-else-if="isStale"
      class="p-3 rounded-md stale_banner flex items-start gap-2"
    >
      <ThemeImage
        :icon="imageNames.YELLOW_WARNING"
        alt="stale"
        class="size-4 mt-0.5"
      />
      <p class="text-xs text_color">{{ COPY.STALE_DNS_HINT }}</p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { message } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import {
  EXTERNAL_DOMAIN_STATUS,
  EXTERNAL_DOMAINS_COPY as COPY,
  PROVISIONING_STEPS,
  STALE_DNS_MS,
} from "@/src/constants/externalDomainsConstants.js";

const props = defineProps({
  record: { type: Object, required: true },
  startedAt: { type: Number, default: () => Date.now() },
});

const recordId = computed(() => props.record?.id);
const domain = computed(() => props.record?.domain || "");
const status = computed(() => props.record?.status);
const errorMessage = computed(() => props.record?.error_message || "");
const expectedARecords = computed(
  () => props.record?.expected_a_records || [],
);
const currentARecords = computed(
  () => props.record?.current_a_records || [],
);

const statusOrder = {
  [EXTERNAL_DOMAIN_STATUS.PENDING_DNS]: 0,
  [EXTERNAL_DOMAIN_STATUS.DNS_VERIFIED]: 1,
  [EXTERNAL_DOMAIN_STATUS.CERT_PENDING]: 2,
  [EXTERNAL_DOMAIN_STATUS.PARKED]: 3,
  [EXTERNAL_DOMAIN_STATUS.ACTIVE]: 3,
};

const currentIndex = computed(() => statusOrder[status.value] ?? -1);

const stepReached = (stepKey) =>
  currentIndex.value >= (statusOrder[stepKey] ?? 0);

const stepIcon = (stepKey) => {
  if (status.value === EXTERNAL_DOMAIN_STATUS.FAILED) {
    return imageNames.WARNING;
  }
  return stepReached(stepKey)
    ? imageNames.CHECK_GREEN
    : imageNames.CHECK_CIRCLE_OUTLINED;
};

const currentHasMatch = (ip) => currentARecords.value.includes(ip);

const isFailed = computed(
  () => status.value === EXTERNAL_DOMAIN_STATUS.FAILED,
);

// Reactive "now" ticks every 30s so isStale recomputes while status stays
// PENDING_DNS. Without this, Vue 3.4+ caches isStale because its only other
// dep (status.value) stops changing, and the banner never appears.
const now = ref(Date.now());
let staleTick = null;
onMounted(() => {
  staleTick = setInterval(() => {
    now.value = Date.now();
  }, 30_000);
});
onUnmounted(() => {
  if (staleTick) clearInterval(staleTick);
});

const isStale = computed(
  () =>
    status.value === EXTERNAL_DOMAIN_STATUS.PENDING_DNS &&
    now.value - (props.startedAt || now.value) >= STALE_DNS_MS,
);

const copyValue = async (value) => {
  try {
    await navigator.clipboard.writeText(value);
    message.success("Copied");
  } catch (error) {
    message.error("Copy failed");
  }
};
</script>

<style scoped>
.a_records_container {
  background: var(--white-opacity-6);
}

.failed_banner {
  background: var(--red-tag-bg);
}

.stale_banner {
  background: var(--yellow-tag-bg);
}
</style>
